import { create } from 'zustand'
import { ACTIVE_COMPANY_KEY, ACTIVE_OUTLET_KEY, api, tokenStorage } from '@/api/client'
import type { CurrentUser, TokenResponse } from '@/api/types'

/** What the login page has to act on. */
export type SignInOutcome =
  | { codeRequired: false }
  | { codeRequired: true; challenge: string | null; sentTo: string | null; validMinutes: number }

interface AuthState {
  user: CurrentUser | null
  status: 'unknown' | 'authenticated' | 'anonymous'
  /** Outlet the user is currently working in; null means every accessible outlet. */
  activeOutletId: string | null
  /** Business being looked at; one of the user's companies. */
  activeCompanyId: string | null
  /**
   * Offers the password. Answers either "you are in" or "a code is waiting",
   * because the second is not a failure and must not be thrown.
   */
  signIn: (username: string, password: string) => Promise<SignInOutcome>
  /**
   * Sign in with a code only: one is texted to the account's phone. Takes a
   * username, or the mobile number on the account standing in for one.
   */
  requestCode: (identifier: string) => Promise<SignInOutcome>
  /** The second step, with the code off the phone. */
  signInWithCode: (challenge: string, code: string) => Promise<void>
  /** Forgotten password, step one: a code to the account's mobile. */
  forgotPassword: (identifier: string) => Promise<SignInOutcome>
  /** Forgotten password, step two: the code answered, and a password set. */
  resetPassword: (challenge: string, code: string, newPassword: string) => Promise<void>
  /** Takes a set of tokens and becomes that person. */
  adopt: (tokens: TokenResponse) => void
  signOut: () => Promise<void>
  restore: () => Promise<void>
  setActiveOutlet: (outletId: string | null) => void
  setActiveCompany: (companyId: string) => void
  can: (permission: string) => boolean
  /** Create or edit within a module, whichever of the two they hold. */
  canWrite: (module: string) => boolean
  hasRole: (role: string) => boolean
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  status: 'unknown',
  activeOutletId: localStorage.getItem(ACTIVE_OUTLET_KEY),
  activeCompanyId: localStorage.getItem(ACTIVE_COMPANY_KEY),

  async signIn(username, password) {
    const answer = await api.post<TokenResponse>('/api/v1/auth/login', { username, password })
    // The password may not be the whole answer: where the business asks for a
    // code, nothing is signed in yet and the page has one more question to put.
    if (answer.codeRequired) {
      return {
        codeRequired: true as const,
        challenge: answer.challenge ?? null,
        sentTo: answer.sentTo ?? null,
        validMinutes: answer.validMinutes ?? 0,
      }
    }
    get().adopt(answer)
    return { codeRequired: false as const }
  },

  async requestCode(identifier) {
    const answer = await api.post<TokenResponse>('/api/v1/auth/otp/request', { identifier })
    return {
      codeRequired: true as const,
      challenge: answer.challenge ?? null,
      sentTo: answer.sentTo ?? null,
      validMinutes: answer.validMinutes ?? 0,
    }
  },

  async signInWithCode(challenge, code) {
    get().adopt(await api.post<TokenResponse>('/api/v1/auth/otp', { challenge, code }))
  },

  async forgotPassword(identifier) {
    const answer = await api.post<TokenResponse>('/api/v1/auth/password/forgot', { identifier })
    return {
      codeRequired: true as const,
      challenge: answer.challenge ?? null,
      sentTo: answer.sentTo ?? null,
      validMinutes: answer.validMinutes ?? 0,
    }
  },

  async resetPassword(challenge, code, newPassword) {
    get().adopt(
      await api.post<TokenResponse>('/api/v1/auth/password/reset', { challenge, code, newPassword }),
    )
  },

  /** Takes the tokens and becomes that person. */
  adopt(tokens: TokenResponse) {
    tokenStorage.save(tokens)
    // Land in their home company. Whatever was switched to last time belonged
    // to whoever was signed in then, who may not be this person.
    localStorage.setItem(ACTIVE_COMPANY_KEY, tokens.user.companyId)
    // And in all of it until they narrow down, for the same reason: the branch
    // somebody else was standing in is not this person's business.
    localStorage.removeItem(ACTIVE_OUTLET_KEY)
    set({
      user: tokens.user,
      status: 'authenticated',
      activeCompanyId: tokens.user.companyId,
      activeOutletId: null,
    })
  },

  async signOut() {
    try {
      await api.post('/api/v1/auth/logout')
    } catch {
      // Signing out locally matters more than telling the server about it.
    }
    tokenStorage.clear()
    localStorage.removeItem(ACTIVE_COMPANY_KEY)
    localStorage.removeItem(ACTIVE_OUTLET_KEY)
    set({ user: null, status: 'anonymous', activeOutletId: null, activeCompanyId: null })
  },

  /** Rehydrates the session on a page reload using the stored token. */
  async restore() {
    if (!tokenStorage.access()) {
      set({ status: 'anonymous' })
      return
    }
    try {
      const user = await api.get<CurrentUser>('/api/v1/auth/me')
      set({ user, status: 'authenticated' })
    } catch {
      tokenStorage.clear()
      set({ user: null, status: 'anonymous' })
    }
  },

  /**
   * Narrows every screen to one branch, or to all of them again.
   *
   * <p>Survives a reload: somebody who works in one branch sets it once and
   * should not have to say so again every morning.</p>
   */
  setActiveOutlet(outletId) {
    if (outletId) {
      localStorage.setItem(ACTIVE_OUTLET_KEY, outletId)
    } else {
      localStorage.removeItem(ACTIVE_OUTLET_KEY)
    }
    set({ activeOutletId: outletId })
  },

  /**
   * Switches which business is being looked at.
   *
   * <p>Everything on screen belongs to one company, so nothing cached from the
   * last one may survive the switch - the caller clears the query cache.</p>
   *
   * <p>The branch goes with it. An outlet belongs to one company, so the one
   * being worked in is meaningless in the next; left set, every screen would
   * filter on a branch this business does not have and show nothing.</p>
   */
  setActiveCompany(companyId) {
    localStorage.setItem(ACTIVE_COMPANY_KEY, companyId)
    localStorage.removeItem(ACTIVE_OUTLET_KEY)
    set({ activeCompanyId: companyId, activeOutletId: null })
  },

  can(permission) {
    const { user } = get()
    if (!user) return false
    // The permissions the server sent, and nothing else. Treating SUPER_ADMIN
    // as holding everything used to be a harmless shortcut - the reference data
    // grants it every permission there is. It stopped being harmless once one
    // permission was deliberately withheld from it: platform.admin, which
    // reaches across companies. The button appeared and the server refused it.
    return user.permissions.includes(permission)
  },

  /**
   * Whether this module can be worked in at all.
   *
   * <p>Creating, editing and deleting are granted separately, so an office can
   * have a clerk who enters work but cannot remove it. A screen that only asks
   * "may this person change anything here" asks this instead of naming a verb;
   * the buttons that do one particular thing name their own.</p>
   */
  canWrite(module) {
    const { can } = get()
    return can(`${module}.create`) || can(`${module}.edit`)
  },

  hasRole(role) {
    return get().user?.roles.includes(role) ?? false
  },
}))

// The API client raises this when a refresh fails, so the UI can drop to the login screen.
window.addEventListener('catering:signed-out', () => {
  useAuthStore.setState({ user: null, status: 'anonymous' })
})
